/**
 * 事项看板（`#planner`）：一轮里的第 ② 步——按诉求记账（建事项、并信息、撤销、拆单），产出本轮要推进的事项；不选工具、不调工具
 * （后台对齐批 4；摸底 C_应答配置 第 10 条，D-07「计划器」改名「事项看板」：代码里叫 planner 的其实是召回，真正建计划的是事项板）。
 * 五个页签全部只读，数据取运行时单源（`GET /board/schema`）：事项字段（`plan_state.new_item` ＋ `contracts.Item`）、
 * 状态与合法流转（`plan_state` 流转表）、落账规则（规则注册表 stage=事项板 ／ 结算）、预算与上限（生效配置快照 ＋ 默认值）；
 * 会话事项快照取每轮根事件的本轮末事项清单与 `plan.item_event`（`GET /board/recent`，近 3 天，盲测会话后端已排除）。
 * 规则编号都能跳「运行规则」页对应那条。改字段、改规则走 PR。
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { JsonBlock } from "@/components/ui/code-block";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import { Tabs } from "@/components/ui/tabs";
import {
	type PlanBoardEvent,
	type PlanBoardItem,
	type PlanBoardItemDetail,
	type PlanBoardRule,
	type PlanBoardSchema,
	type PlanBoardTurn,
	planErrorMessage,
	usePlanBoardRecent,
	usePlanBoardSchema,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { PageJump } from "../kit/page-jump";
import {
	ITEM_STATUS_TONE,
	LAYER_ZH,
	SOURCE_TONE,
	SOURCE_ZH,
	STOP_REASON_TONE,
	STOP_REASON_ZH,
	orderNo,
	zh,
} from "../kit/plan-labels";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";
import { SKILL_ZH } from "../trace-drawer/execution-drawer";

type TabKey = "fields" | "states" | "rules" | "budget" | "recent";
const TABS: { key: TabKey; label: string }[] = [
	{ key: "fields", label: "事项定义" },
	{ key: "states", label: "状态与流转" },
	{ key: "rules", label: "落账规则" },
	{ key: "budget", label: "预算与上限" },
	{ key: "recent", label: "会话事项快照" },
];

function Trunc({ text, mono = false }: { text?: string | null; mono?: boolean }) {
	if (!text) return <span className="text-text-muted">—</span>;
	return (
		<span
			className={`block truncate text-text-secondary${mono ? " font-mono text-xs" : ""}`}
			title={text}
		>
			{text}
		</span>
	);
}

/** 规则编号：点了去「运行规则」页并打开那条 */
function RuleLink({ id }: { id: string }) {
	return (
		<Link
			to="/plan-lab"
			search={{ rule: id }}
			hash="guards"
			title={`去运行规则看 ${id}`}
			onClick={(e) => e.stopPropagation()}
			className="font-mono text-xs text-text-secondary underline-offset-2 hover:text-brand-accent hover:underline"
			data-testid="plan-board-rule-link"
		>
			{id}
		</Link>
	);
}

function RuleLinks({ ids }: { ids: string[] }) {
	if (!ids.length) return <span className="text-text-muted">—</span>;
	return (
		<span className="flex flex-wrap gap-x-2">
			{ids.map((r) => (
				<RuleLink key={r} id={r} />
			))}
		</span>
	);
}

function Section({
	title,
	note,
	children,
	testId,
}: {
	title: string;
	note?: React.ReactNode;
	children: React.ReactNode;
	testId?: string;
}) {
	return (
		<section className="space-y-2" data-testid={testId}>
			<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
				<h3 className="text-sm font-semibold text-text-primary">{title}</h3>
				{note ? <span className="text-xs text-text-muted">{note}</span> : null}
			</div>
			{children}
		</section>
	);
}

function show(v: unknown): string {
	if (v === null || v === undefined) return "null";
	if (typeof v === "string") return v === "" ? '""' : v;
	return JSON.stringify(v);
}

export function BoardTab() {
	const [tab, setTab] = useState<TabKey>("fields");
	const q = usePlanBoardSchema();
	const data = q.data;

	return (
		<div className="space-y-3" data-testid="plan-block-board">
			<Tabs items={TABS} active={tab} onChange={setTab} />
			{q.isLoading ? (
				<Card>
					<CardContent className="py-4">
						<LoadingInline />
					</CardContent>
				</Card>
			) : q.isError || !data ? (
				<Card>
					<CardContent>
						<FailState
							reason={planErrorMessage(q.error)}
							onRetry={() => q.refetch()}
							retrying={q.isFetching}
							testId="plan-board-fail"
						/>
					</CardContent>
				</Card>
			) : (
				<>
					{tab === "fields" ? <FieldsPanel data={data} /> : null}
					{tab === "states" ? <StatesPanel data={data} /> : null}
					{tab === "rules" ? <RulesPanel data={data} /> : null}
					{tab === "budget" ? <BudgetPanel data={data} /> : null}
					{tab === "recent" ? <RecentPanel data={data} /> : null}
				</>
			)}
		</div>
	);
}

/* ── ① 事项定义 ─────────────────────────────────────── */

function FieldsPanel({ data }: { data: PlanBoardSchema }) {
	type F = PlanBoardSchema["fields"][number];
	type O = PlanBoardSchema["object_fields"][number];
	const groups = useMemo(() => [...new Set(data.fields.map((f) => f.group))], [data.fields]);
	const cols: PlanColumn<F>[] = [
		{
			key: "key",
			title: "字段",
			className: "w-44",
			render: (f) => <span className="font-mono text-xs">{f.key}</span>,
		},
		{
			key: "desc",
			title: "是什么",
			className: "max-w-[640px]",
			render: (f) => <Trunc text={f.desc} />,
		},
		{
			key: "default",
			title: "初始值",
			className: "w-48 max-w-[200px]",
			render: (f) =>
				f.in_skeleton ? (
					<Trunc mono text={show(f.default)} />
				) : (
					<span className="text-text-muted">建事项时另加</span>
				),
		},
	];
	const objCols: PlanColumn<O>[] = [
		{
			key: "key",
			title: "子字段",
			className: "w-44",
			render: (o) => <span className="font-mono text-xs">{o.key}</span>,
		},
		{
			key: "desc",
			title: "是什么",
			className: "max-w-[640px]",
			render: (o) => <Trunc text={o.desc} />,
		},
		{
			key: "default",
			title: "初始值",
			className: "w-48 max-w-[200px]",
			render: (o) => <Trunc mono text={show(o.default)} />,
		},
	];
	return (
		<Card>
			<CardContent className="space-y-5 py-4" data-testid="plan-board-fields">
				<p className="text-xs text-text-muted">
					一件事项＝一件诉求落到一个对象上：要办什么、对谁办、知道了什么、办到哪一步、在等客户什么。字段取自{" "}
					<span className="font-mono">plan_state.new_item</span> 与层间契约{" "}
					<span className="font-mono">contracts.Item</span>（v{data.item_contract_version}
					），随会话状态往返。哪些意图不建事项见 <PageJump to="tags" />。
				</p>
				{groups.map((g) => (
					<Section key={g} title={g}>
						<PlanTable
							columns={cols}
							rows={data.fields.filter((f) => f.group === g)}
							rowKey={(f) => f.key}
							density="compact"
							rowTestId="plan-board-field-row"
						/>
					</Section>
				))}
				<Section title="对象 object 的子字段" note="多笔订单建事项时一单一件">
					<PlanTable
						columns={objCols}
						rows={data.object_fields}
						rowKey={(o) => o.key}
						density="compact"
					/>
				</Section>
				<div className="text-xs text-text-muted" data-testid="plan-board-fields-count">
					共 {data.fields.length} 个字段，其中建事项时另加{" "}
					{data.fields.filter((f) => !f.in_skeleton).length} 个
				</div>
			</CardContent>
		</Card>
	);
}

/* ── ② 状态与流转 ───────────────────────────────────── */

const START = "（新建）";

function StatesPanel({ data }: { data: PlanBoardSchema }) {
	type S = PlanBoardSchema["statuses"][number];
	type T = PlanBoardSchema["transitions"][number];
	type W = PlanBoardSchema["wait_kinds"][number];
	const label = useMemo(
		() => Object.fromEntries(data.statuses.map((s) => [s.key, s.label])),
		[data.statuses],
	);
	const stLabel = (k: string | null) => (k === null ? START : (label[k] ?? k));
	const StBadge = ({ k }: { k: string | null }) =>
		k === null ? (
			<span className="text-text-muted">{START}</span>
		) : (
			<Badge tone={ITEM_STATUS_TONE[k] ?? "muted"}>{stLabel(k)}</Badge>
		);
	const statusCols: PlanColumn<S>[] = [
		{
			key: "key",
			title: "状态",
			className: "w-36",
			render: (s) => (
				<span className="flex items-center gap-2">
					<Badge tone={ITEM_STATUS_TONE[s.key] ?? "muted"}>{s.label}</Badge>
					<span className="font-mono text-xs text-text-muted">{s.key}</span>
				</span>
			),
		},
		{
			key: "desc",
			title: "意思",
			className: "max-w-[620px]",
			render: (s) => <Trunc text={s.desc} />,
		},
		{
			key: "terminal",
			title: "终态",
			className: "w-20",
			render: (s) =>
				s.terminal ? (
					<Badge tone="neutral">终态</Badge>
				) : (
					<span className="text-text-muted">—</span>
				),
		},
	];
	const tCols: PlanColumn<T>[] = [
		{ key: "from", title: "从", className: "w-28", render: (t) => <StBadge k={t.from} /> },
		{ key: "to", title: "到", className: "w-28", render: (t) => <StBadge k={t.to} /> },
		{
			key: "why",
			title: "什么时候走",
			className: "min-w-[360px] max-w-[620px]",
			render: (t) => <Trunc text={t.why} />,
		},
		{ key: "rules", title: "规则", className: "w-44", render: (t) => <RuleLinks ids={t.rules} /> },
	];
	const wCols: PlanColumn<W>[] = [
		{
			key: "key",
			title: "等待类型",
			className: "w-40",
			render: (w) => (
				<span className="flex items-center gap-2">
					<span className="text-text-primary">{w.label}</span>
					<span className="font-mono text-xs text-text-muted">{w.key}</span>
				</span>
			),
		},
		{
			key: "desc",
			title: "在等客户什么",
			className: "max-w-[620px]",
			render: (w) => (
				<Trunc text={w.in_enum ? w.desc : `${w.desc}（不在 WAIT_KINDS 枚举里，代码直接写）`} />
			),
		},
	];
	// 简单状态图：行＝从（含新建），列＝到；有合法流转的格子打点
	const froms: (string | null)[] = [
		null,
		...data.statuses.filter((s) => !s.terminal).map((s) => s.key),
	];
	const allowed = new Set(data.transitions.map((t) => `${t.from ?? ""}>${t.to}`));
	const terminal = data.statuses.filter((s) => s.terminal).map((s) => s.label);
	return (
		<Card>
			<CardContent className="space-y-5 py-4" data-testid="plan-board-states">
				<Section
					title="状态"
					note={
						<>
							{data.statuses.length} 个；终态（{terminal.join("、")}）不回头
						</>
					}
				>
					<PlanTable
						columns={statusCols}
						rows={data.statuses}
						rowKey={(s) => s.key}
						density="compact"
						rowTestId="plan-board-status-row"
					/>
				</Section>

				<Section
					title="流转图"
					note={
						<>
							行是「从」、列是「到」；只许这些格子，不合法的变更丢弃并留痕（规则{" "}
							<RuleLink id={data.transition_rule} />）
						</>
					}
					testId="plan-board-matrix"
				>
					<div className="overflow-x-auto">
						<div
							className="grid w-max items-center text-xs"
							style={{ gridTemplateColumns: `7rem repeat(${data.statuses.length}, 5.5rem)` }}
						>
							<div className="px-2 py-1 text-text-muted">从 ＼ 到</div>
							{data.statuses.map((s) => (
								<div key={s.key} className="px-2 py-1 text-center">
									<Badge tone={ITEM_STATUS_TONE[s.key] ?? "muted"}>{s.label}</Badge>
								</div>
							))}
							{froms.map((f) => [
								<div key={`${f ?? "start"}:h`} className="border-t border-surface-border px-2 py-1">
									<StBadge k={f} />
								</div>,
								...data.statuses.map((s) => (
									<div
										key={`${f ?? "start"}:${s.key}`}
										className="border-t border-surface-border px-2 py-1 text-center"
									>
										{allowed.has(`${f ?? ""}>${s.key}`) ? (
											<span
												className="text-brand-accent"
												title={`${stLabel(f)} → ${s.label}`}
												data-testid="plan-board-matrix-dot"
											>
												●
											</span>
										) : (
											<span className="text-text-muted">·</span>
										)}
									</div>
								)),
							])}
						</div>
					</div>
				</Section>

				<Section title="合法流转" note={`流转表 ${data.transitions.length} 条`}>
					<PlanTable
						columns={tCols}
						rows={data.transitions}
						rowKey={(t) => `${t.from ?? ""}>${t.to}`}
						density="compact"
						rowTestId="plan-board-transition-row"
					/>
				</Section>

				<Section title="等客户时在等什么" note="离开等客户即清空">
					<PlanTable
						columns={wCols}
						rows={data.wait_kinds}
						rowKey={(w) => w.key}
						density="compact"
					/>
				</Section>

				<Section title="办不了的原因">
					<ul className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
						{data.cant_reasons.map((c) => (
							<li key={c.key} className="flex items-baseline gap-2">
								<span className="w-24 shrink-0 font-mono text-xs text-text-secondary">{c.key}</span>
								<span className="truncate text-text-secondary" title={c.desc}>
									{c.desc}
								</span>
							</li>
						))}
					</ul>
				</Section>
			</CardContent>
		</Card>
	);
}

/* ── ③ 落账规则 ─────────────────────────────────────── */

function ruleCols(): PlanColumn<PlanBoardRule>[] {
	return [
		{ key: "id", title: "编号", className: "w-32", render: (r) => <RuleLink id={r.id} /> },
		{
			key: "name",
			title: "名称",
			className: "max-w-[200px]",
			render: (r) => (
				<span className="block truncate" title={r.name}>
					{r.name}
				</span>
			),
		},
		{
			key: "does",
			title: "做什么",
			className: "max-w-[640px]",
			render: (r) => <Trunc text={r.does} />,
		},
		{
			key: "kind",
			title: "类型",
			className: "w-24",
			render: (r) => <Badge tone="muted">{r.kind}</Badge>,
		},
	];
}

function RulesPanel({ data }: { data: PlanBoardSchema }) {
	const navigate = useNavigate();
	const byId = useMemo(() => new Map(data.rules.map((r) => [r.id, r])), [data.rules]);
	const cols = ruleCols();
	const open = (r: PlanBoardRule) =>
		navigate({ to: "/plan-lab", search: { rule: r.id }, hash: "guards" });
	const settle = data.rules.filter((r) => r.stage === "结算");
	const boardCount = data.rules.filter((r) => r.stage === "事项板").length;
	return (
		<Card>
			<CardContent className="space-y-5 py-4" data-testid="plan-board-rules">
				<p className="text-xs text-text-muted">
					事项板只记账、不决定怎么办：每一轮按信号拆出的诉求逐件落账。下面按动作分组，规则全部取自运行规则注册表（环节＝事项板{" "}
					{boardCount} 条）；点一行去运行规则页。确认门的判定顺序见{" "}
					<Link
						to="/plan-lab"
						hash="signal"
						className="underline-offset-2 hover:text-brand-accent hover:underline"
					>
						信号识别
					</Link>
					。
				</p>
				{data.groups.map((g) => (
					<Section key={g.key} title={g.title} note={g.line} testId={`plan-board-group-${g.key}`}>
						<PlanTable
							columns={cols}
							rows={g.rules.map((i) => byId.get(i)).filter((r): r is PlanBoardRule => !!r)}
							rowKey={(r) => r.id}
							onRowClick={open}
							density="compact"
							rowTestId="plan-board-rule-row"
						/>
					</Section>
				))}
				<Section
					title="结算（推进之后）"
					note={`本轮唯一问题、无进展计数与上限、可说口径 · ${settle.length} 条`}
					testId="plan-board-group-settle"
				>
					<PlanTable
						columns={cols}
						rows={settle}
						rowKey={(r) => r.id}
						onRowClick={open}
						density="compact"
						rowTestId="plan-board-settle-row"
					/>
				</Section>
			</CardContent>
		</Card>
	);
}

/* ── ④ 预算与上限 ───────────────────────────────────── */

function BudgetPanel({ data }: { data: PlanBoardSchema }) {
	type B = PlanBoardSchema["budget"]["rows"][number];
	const b = data.budget;
	const cols: PlanColumn<B>[] = [
		{
			key: "label",
			title: "上限",
			className: "max-w-[220px]",
			render: (r) => (
				<span className="block truncate text-text-primary" title={`${r.label}（${r.key}）`}>
					{r.label}
				</span>
			),
		},
		{
			key: "key",
			title: "配置项",
			className: "w-48",
			render: (r) => <span className="font-mono text-xs">{r.key}</span>,
		},
		{
			key: "value",
			title: "当前值",
			className: "w-20",
			align: "right",
			render: (r) => (
				<span className="font-mono" data-testid="plan-board-budget-value">
					{show(r.value)}
				</span>
			),
		},
		{
			key: "default",
			title: "默认",
			className: "w-24",
			render: (r) =>
				show(r.value) === show(r.default) ? (
					<span className="text-text-muted">{show(r.default)}（同）</span>
				) : (
					<Badge tone="warning">默认 {show(r.default)}</Badge>
				),
		},
		{
			key: "does",
			title: "作用（到了怎么办）",
			className: "max-w-[380px]",
			render: (r) => <Trunc text={r.does} />,
		},
		{
			key: "rule",
			title: "规则",
			className: "w-20",
			render: (r) => (r.rule_id ? <RuleLink id={r.rule_id} /> : "—"),
		},
	];
	return (
		<Card>
			<CardContent className="space-y-5 py-4" data-testid="plan-board-budget">
				<Section
					title="可配的上限"
					note={
						<>
							生效快照 <span className="font-mono">{b.snapshot_id ?? "—"}</span>
							（缺的项按默认）；只读，改值存新快照后下一轮生效
						</>
					}
				>
					<PlanTable
						columns={cols}
						rows={b.rows}
						rowKey={(r) => r.key}
						density="compact"
						rowTestId="plan-board-budget-row"
					/>
				</Section>
				<Section
					title="写死的计数"
					note={<>不在配置里；无进展看「五样」签名：{data.progress_signature.join("、")}</>}
				>
					<PlanTable
						columns={ruleCols()}
						rows={b.fixed}
						rowKey={(r) => r.id}
						density="compact"
						rowTestId="plan-board-fixed-row"
					/>
				</Section>
			</CardContent>
		</Card>
	);
}

/* ── ⑤ 会话事项快照 ─────────────────────────────────── */

function fmtTime(at: string | null): string {
	if (!at) return "—";
	const d = new Date(at);
	if (Number.isNaN(d.getTime())) return at;
	const p = (n: number) => String(n).padStart(2, "0");
	return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

const OBJECT_KIND_ZH: Record<string, string> = {
	order: "订单",
	line_item: "行项",
	fulfillment_line: "履约行",
	product: "商品",
	code: "优惠码",
};

/** 事项对象的一句话：订单 尾号 ／ 行项 n 个 ／ 商品 ／ 优惠码 ／ 待定 */
function objectText(o: Record<string, unknown>): string {
	const parts: string[] = [];
	const arr = (k: string) => (Array.isArray(o[k]) ? (o[k] as unknown[]) : []);
	if (o.order_id) parts.push(`订单 ${orderNo(o.order_id)}`);
	if (arr("line_ids").length) parts.push(`行项 ${arr("line_ids").length} 个`);
	if (arr("product_ids").length) parts.push(`商品 ${arr("product_ids").map(String).join("、")}`);
	if (arr("codes").length) parts.push(`优惠码 ${arr("codes").map(String).join("、")}`);
	if (o.pending) parts.push(String(o.pending_missing || "对象待定"));
	else if (o.kind === "order" && !o.order_id) parts.push("订单待定");
	return (
		parts.join(" · ") ||
		(o.kind && o.kind !== "none" ? (OBJECT_KIND_ZH[String(o.kind)] ?? String(o.kind)) : "—")
	);
}

function RecentPanel({ data }: { data: PlanBoardSchema }) {
	const [limit, setLimit] = useState("20");
	const q = usePlanBoardRecent(Number(limit));
	const [open, setOpen] = useState<PlanBoardTurn | null>(null);
	const demand = (k?: string | null) => (k ? (data.demand_labels[k] ?? k) : "（无标签）");
	const statusLabel = useMemo(
		() => Object.fromEntries(data.statuses.map((s) => [s.key, s.label])),
		[data.statuses],
	);

	const cols: PlanColumn<PlanBoardTurn>[] = [
		{
			key: "at",
			title: "时间",
			className: "w-32",
			render: (r) => <span className="font-mono text-xs">{fmtTime(r.at)}</span>,
		},
		{
			key: "source",
			title: "来源",
			className: "w-20",
			render: (r) => (
				<Badge tone={SOURCE_TONE[r.source ?? ""] ?? "muted"}>{zh(SOURCE_ZH, r.source)}</Badge>
			),
		},
		{
			key: "turn",
			title: "轮",
			className: "w-12",
			align: "right",
			render: (r) => r.turn_no ?? "—",
		},
		{
			key: "message",
			title: "客户原话",
			className: "max-w-[240px]",
			render: (r) => (
				<span className="block truncate text-text-primary" title={r.message}>
					{r.message || "—"}
				</span>
			),
		},
		{
			key: "items",
			title: "本轮末事项",
			className: "max-w-[300px]",
			render: (r) =>
				r.items.length ? (
					<span className="flex flex-wrap gap-1">
						{r.items.slice(0, 4).map((i) => (
							<Badge key={i.item_id} tone={ITEM_STATUS_TONE[i.status] ?? "muted"}>
								{i.item_id.replace(/^it-/, "#")} {statusLabel[i.status] ?? i.status}
							</Badge>
						))}
						{r.items.length > 4 ? (
							<span className="text-xs text-text-muted">+{r.items.length - 4}</span>
						) : null}
					</span>
				) : (
					<span className="text-text-muted">无事项</span>
				),
		},
		{
			key: "changed",
			title: "本轮有变化",
			className: "w-24",
			render: (r) => {
				const n = r.items.filter((i) => i.changed).length;
				return n ? `${n} 件` : <span className="text-text-muted">—</span>;
			},
		},
		{
			key: "stop",
			title: "结束原因",
			className: "w-32",
			render: (r) =>
				r.stop_reason ? (
					<Badge tone={STOP_REASON_TONE[r.stop_reason] ?? "muted"}>
						{zh(STOP_REASON_ZH, r.stop_reason)}
					</Badge>
				) : (
					"—"
				),
		},
	];

	return (
		<Card>
			<CardContent className="space-y-3 py-4" data-testid="plan-board-recent">
				<div className="flex flex-wrap items-center gap-2">
					<LabeledSelect
						label="条数"
						value={limit}
						onChange={setLimit}
						options={["20", "50", "100"].map((v) => ({ value: v, label: `最近 ${v} 轮` }))}
					/>
					<span className="text-xs text-text-muted">每轮结束时的事项清单；盲测题的会话不展示</span>
				</div>
				{q.isLoading ? (
					<LoadingInline />
				) : q.isError || !q.data ? (
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-board-recent-fail"
					/>
				) : q.data.rows.length ? (
					<>
						<PlanTable
							columns={cols}
							rows={q.data.rows}
							rowKey={(r) => r.trace_id}
							onRowClick={setOpen}
							density="compact"
							rowTestId="plan-board-turn-row"
						/>
						<div className="text-xs text-text-muted" data-testid="plan-board-recent-count">
							近 {q.data.days} 天 · 共 {q.data.rows.length} 轮
							{q.data.skipped_sealed ? ` · 跳过盲测 ${q.data.skipped_sealed} 轮` : ""}
							{q.data.truncated ? " · 扫描到上限，更早的没读" : ""}
						</div>
					</>
				) : (
					<EmptyState
						title="近 3 天没有事项留痕"
						desc="在「人工提问」问一句，或跑一批模拟评测后再来看。"
					/>
				)}
			</CardContent>
			<Drawer
				open={!!open}
				onClose={() => setOpen(null)}
				width="min(900px,95vw)"
				testId="plan-board-turn-drawer"
				title={<span className="truncate">「{open?.message}」</span>}
				description={
					open
						? `${fmtTime(open.at)} · ${zh(SOURCE_ZH, open.source)}${open.turn_no ? ` · 第 ${open.turn_no} 轮` : ""} · 计划第 ${open.plan_version ?? "—"} 版 · ${open.trace_id}`
						: undefined
				}
				badges={
					open?.stop_reason ? (
						<Badge tone={STOP_REASON_TONE[open.stop_reason] ?? "muted"}>
							{zh(STOP_REASON_ZH, open.stop_reason)}
						</Badge>
					) : null
				}
			>
				{open ? (
					<TurnDetail t={open} data={data} demand={demand} statusLabel={statusLabel} />
				) : null}
			</Drawer>
		</Card>
	);
}

function TurnDetail({
	t,
	data,
	demand,
	statusLabel,
}: {
	t: PlanBoardTurn;
	data: PlanBoardSchema;
	demand: (k?: string | null) => string;
	statusLabel: Record<string, string>;
}) {
	const waitLabel = Object.fromEntries(data.wait_kinds.map((w) => [w.key, w.label]));
	const iCols: PlanColumn<PlanBoardItem>[] = [
		{
			key: "id",
			title: "事项",
			className: "w-16",
			render: (i) => (
				<span className="font-mono text-xs">
					{i.item_id}
					{t.asked_item_id === i.item_id ? <span className="text-brand-accent"> ?</span> : null}
				</span>
			),
		},
		{
			key: "demand",
			title: "诉求",
			className: "w-28",
			render: (i) => <Trunc text={demand(i.demand_type)} />,
		},
		{
			key: "object",
			title: "对象",
			className: "max-w-[200px]",
			render: (i) => <Trunc text={objectText(i.object)} />,
		},
		{
			key: "status",
			title: "状态",
			className: "w-24",
			render: (i) => (
				<Badge tone={ITEM_STATUS_TONE[i.status] ?? "muted"}>
					{statusLabel[i.status] ?? i.status}
				</Badge>
			),
		},
		{
			key: "wait",
			title: "等待",
			className: "w-24",
			render: (i) =>
				i.wait_kind ? (
					(waitLabel[i.wait_kind] ?? i.wait_kind)
				) : (
					<span className="text-text-muted">—</span>
				),
		},
		{
			key: "changed",
			title: "本轮",
			className: "w-24",
			render: (i) =>
				i.new ? (
					<Badge tone="accent">新建</Badge>
				) : i.changed ? (
					<Badge tone="info">有变化</Badge>
				) : (
					<span className="text-text-muted">—</span>
				),
		},
		{
			key: "markers",
			title: "标记",
			className: "max-w-[140px]",
			render: (i) => <CellTags mono max={2} items={i.markers} />,
		},
	];
	const eCols: PlanColumn<PlanBoardEvent & { n: number }>[] = [
		{
			key: "at",
			title: "时间",
			className: "w-24",
			render: (e) => <span className="font-mono text-xs">{fmtTime(e.at).slice(6)}</span>,
		},
		{
			key: "layer",
			title: "环节",
			className: "w-20",
			render: (e) => <span className="text-text-secondary">{zh(LAYER_ZH, e.layer)}</span>,
		},
		{
			key: "item",
			title: "事项",
			className: "w-16",
			render: (e) => <span className="font-mono text-xs">{e.item_id ?? "—"}</span>,
		},
		{
			key: "kind",
			title: "事件",
			className: "w-36",
			render: (e) => (
				<span className="block truncate text-text-primary" title={e.kind}>
					{data.event_kinds[e.kind] ?? e.kind}
				</span>
			),
		},
		{
			key: "attrs",
			title: "内容",
			className: "max-w-[420px]",
			render: (e) => <Trunc mono text={eventBrief(e, statusLabel)} />,
		},
	];
	const events = t.events.map((e, n) => ({ ...e, n }));
	return (
		<>
			<DrawerSection title={`本轮末事项（${t.items.length}）`} testId="plan-board-turn-items">
				{t.items.length ? (
					<PlanTable columns={iCols} rows={t.items} rowKey={(i) => i.item_id} density="compact" />
				) : (
					<span className="text-sm text-text-muted">这一轮结束时没有事项（闲聊或整轮作废）。</span>
				)}
				{t.asked_item_id ? (
					<div className="text-xs text-text-muted">
						带 <span className="text-brand-accent">?</span> 的是本轮问客户的那件（{t.asked_item_id}
						）
					</div>
				) : null}
			</DrawerSection>
			<ItemDetails items={t.items} />
			<DrawerSection title={`事项事件时间线（${t.events.length}）`} testId="plan-board-turn-events">
				{events.length ? (
					<PlanTable
						columns={eCols}
						rows={events}
						rowKey={(e) => String(e.n)}
						density="compact"
						rowTestId="plan-board-event-row"
					/>
				) : (
					<span className="text-sm text-text-muted">这一轮没有事项事件（事项都没动）。</span>
				)}
			</DrawerSection>
			<DrawerSection title="完整 JSON 摘要">
				<JsonBlock value={t} maxHeight={360} />
			</DrawerSection>
		</>
	);
}

const BLOCKED_BY_ZH: Record<string, string> = {
	"KNO-03": "店铺政策不允许",
	PRE: "前置条件不成立",
	cannot: "工具返回不可办",
	policy_code: "写权限未开放",
};

const toolZh = (t?: string | null) => (t ? (SKILL_ZH[t] ?? t) : "—");

/** 一件事项要专门说清的状态，逐条写成业务话（没有就不出这一行） */
function detailLines(d: PlanBoardItemDetail): string[] {
	const out: string[] = [];
	if (d.split_from) out.push(`从 ${d.split_from} 按订单拆出来的`);
	if (d.follow_up_of) out.push(`追问已办完的 ${d.follow_up_of} 另建的`);
	if (d.inherited_steps?.length)
		out.push(
			`带上了 ${d.inherited_steps[0]?.from ?? "母事项"} 查到的 ${d.inherited_steps.length} 步：${d.inherited_steps
				.map((x) => `第 ${x.step} 步 ${toolZh(x.tool_id)}`)
				.join("、")}（办完 ／ 办不了可引用）`,
		);
	for (const b of d.write_blocked ?? [])
		out.push(
			`${toolZh(b.tool_id)}被拦过：${BLOCKED_BY_ZH[b.by] ?? b.by}${b.why ? `（${b.why}）` : ""}`,
		);
	const c = d.confirm_card;
	if (c) {
		out.push(`等客户确认：${toolZh(c.tool_id)}${c.scope ? ` · ${c.scope}` : ""}`);
		if (c.shown)
			out.push(
				c.shown.ok === false
					? `请客户确认时说的和要提交的不一致：${[...(c.shown.missing ?? []).map((x) => `没说到 ${x}`), ...(c.shown.conflict ?? []).map((x) => `说成了别的 ${x}`)].join("；")}`
					: "请客户确认时说的和要提交的一致",
			);
		if (c.reconfirm)
			out.push(
				`要重新请客户确认：${c.reconfirm.why ?? ""}${[...(c.reconfirm.missing ?? []), ...(c.reconfirm.conflict ?? [])].length ? `（${[...(c.reconfirm.missing ?? []), ...(c.reconfirm.conflict ?? [])].join("、")}）` : ""}`,
			);
		if (c.held)
			out.push(
				`客户同意时同一句带了追问，这次没提交、先答再确认${c.held.questions?.length ? `：${c.held.questions.join("；")}` : ""}`,
			);
	}
	const h = d.handoff;
	if (h) {
		out.push(
			h.reused_from
				? `本会话已在 ${h.reused_from} 转过人工，并入那次、没有再转`
				: `已转人工${h.reason ? `（兜底：${h.reason}）` : ""}`,
		);
		if (h.summary) out.push(`交接摘要：${h.summary}`);
		if (h.note) out.push(`N2 补充：${h.note}`);
	}
	return out;
}

/** 本轮末事项的专门状态：写被拦过、确认卡、拆单 ／ 追问来源、带过来的步骤（之前只在完整 JSON 里能看到） */
function ItemDetails({ items }: { items: PlanBoardItem[] }) {
	const rows = items
		.map((i) => ({ id: i.item_id, lines: detailLines(i.detail ?? {}) }))
		.filter((r) => r.lines.length);
	if (!rows.length) return null;
	return (
		<DrawerSection title={`事项状态说明（${rows.length}）`} testId="plan-board-turn-item-details">
			<div className="space-y-2">
				{rows.map((r) => (
					<div key={r.id} className="flex gap-3 text-sm" data-testid="plan-board-item-detail">
						<span className="w-14 shrink-0 font-mono text-xs text-text-secondary">{r.id}</span>
						<ul className="min-w-0 space-y-1 text-text-primary">
							{r.lines.map((l) => (
								<li key={l} className="break-words">
									{l}
								</li>
							))}
						</ul>
					</div>
				))}
			</div>
		</DrawerSection>
	);
}

const SPLIT_TOOL = (a: Record<string, unknown>) =>
	`${toolZh(a.from_tool as string)} → ${toolZh(a.to_tool as string)}${a.reason ? `（${String(a.reason)}）` : ""}`;

/** 时间线一行的内容：状态变化写「从 → 到」，其余给属性的紧凑 JSON */
function eventBrief(e: PlanBoardEvent, statusLabel: Record<string, string>): string {
	const a = e.attrs;
	if (e.kind === "state_change") {
		const st = (k: unknown) => (k ? (statusLabel[String(k)] ?? String(k)) : "新建");
		const why = (a.status_reason as { text?: string } | null)?.text;
		return `${st(a.from)} → ${st(a.to)}${why ? `：${why}` : ""}`;
	}
	if (e.kind === "step" && a.action)
		return `${String(a.action)}${a.why ? ` · ${String(a.why)}` : ""}`;
	if (e.kind === "split_retyped") return `这单的状态办不了原诉求，改走 ${SPLIT_TOOL(a)}`;
	if (e.kind === "other_order_as_new")
		return `同一句又点了另一单 ${((a.orders as string[]) ?? []).map((o) => orderNo(o)).join("、")}，另建一件`;
	if (e.kind === "confirm_split") return "一句话同意了几单，这一单的同意分到本事项";
	if (e.kind === "confirm_held_question")
		return "同意的同一句带了追问：先答追问、再请确认，这次没提交";
	if (e.kind === "reject_after_close")
		return "客户对已结束的这件说不要了 ／ 先不用：只记录，不建事项";
	if (e.kind === "mentioned_after_handoff")
		return "已转人工后客户接着说（道谢 ／ 等人工）：只记录，不再转";
	if (e.kind === "held_for_intent")
		return "信号没把握客户真想办（「可能」档）：先问要不要办，不进执行、不出确认卡（BRD-TIER-ASK）";
	if (e.kind === "intent_confirmed") return "客户明确要办：转回办理，之后照常请确认";
	if (e.kind === "intent_still_unsure") return "客户这句仍没说要办：照旧等，不进执行";
	if (e.kind === "slot_shared")
		return `同单 ${String(a.from_item ?? "")} 上客户已给过的 ${String(a.slot ?? "")}，本件缺就直接用，不再追问（BRD-SLOT-SHARE）`;
	if (e.kind === "held_side_question")
		return "在等「要不要办」时客户问了别的事：另起一件交 N2 去答，这件照旧等（BRD-TIER-ASK）";
	if (e.kind === "new_object_after_close")
		return "已结束的写事项上客户提了新的商品：另建一件交 N2，不并进来（BRD-CLOSED-NEW）";
	if (e.kind === "retry_after_cant")
		return "办不了之后客户又提了要求：另建一件、带上对象与查过的结果交 N2 重判（BRD-CLOSED-NEW）";
	if (e.kind === "handoff_summary_appended")
		return "又要转人工：不再调工具，这次的新情况补进那次转人工的交接摘要（EXE-HANDOFF-ONCE）";
	if (e.kind === "handoff_resumed")
		return a.by === "fallback"
			? "挂着的确认有了结果：兜底转人工排到本轮最后补上（EXE-HANDOFF-AFTER-CONFIRM）"
			: "挂着的确认有了结果：转回办理，交 N2 重新决定转人工（EXE-HANDOFF-AFTER-CONFIRM）";
	if (e.kind === "handoff_dequeued")
		return "同轮能办的事项都办到停点了：排队的转人工开始转（EXE-HANDOFF-LAST）";
	if (e.kind === "object_choices_cleared")
		return "客户改了确认卡上的内容：先前选的规格清掉，下一张卡按客户改后的意思重选";
	if (e.kind === "object_denied")
		return `客户否认了 ${((a.denied as { name?: string }[]) ?? []).map((x) => x.name).join("、")}：之后不再拿它提交（EXE-DENIED）`;
	if (e.kind === "object_denial_lifted")
		return `客户又点名了 ${((a.names as string[]) ?? []).join("、")}，解除否认`;
	if (e.kind === "denial_shadow")
		return `否认影子（${a.phase === "lift" ? "解除" : "否认"}，来源 ${String(a.source)}）：旧判定 ${((a.old as string[]) ?? []).join("、") || "无"}，新判定 ${((a.new as string[]) ?? []).join("、") || "无"}${a.order ? "＋这一单" : ""}，${a.agree ? "一致" : "不一致"}；只留痕，不影响提交（EXE-DENIED-SHADOW）`;
	if (e.kind === "object_lines" && a.by === "correct")
		return `客户纠正后，办理行项改为 ${((a.line_ids as string[]) ?? []).map((x) => orderNo(x)).join("、")}`;
	return Object.keys(a).length ? JSON.stringify(a) : "";
}
